import React, { useEffect, useState } from 'react';
import { ParticleBackground } from '../components/ParticleBackground';
import { Navbar } from '../components/Navbar/Navbar';
import { Button } from '../components/common/Button';
import { trackPageView } from '../firebaseConfig';
import { 
  Users, 
  Target, 
  Star, 
  Award, 
  DollarSign, 
  MessageSquare, 
  Share2, 
  Smartphone, 
  TrendingUp,
  CheckCircle,
  Clock,
  Gift,
  Zap,
  BookOpen,
  Video,
  Brain,
  BarChart3,
  Newspaper,
  Lightbulb,
  UserCheck,
  ArrowRight,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AmbassadorGuide: React.FC = () => {
  const navigate = useNavigate();
  const [animationPhase, setAnimationPhase] = useState(0);

  useEffect(() => {
    // Set document title
    document.title = 'MockTrail Ambassador Guide & Playbook - Official Guide';
    
    trackPageView('MockTrail Ambassador Guide');
    setTimeout(() => setAnimationPhase(1), 1800);
    setTimeout(() => setAnimationPhase(2),2100);
    setTimeout(() => setAnimationPhase(3), 2400);
    window.scrollTo(0, 0);
  }, []);

  const targetAudiences = [
    {
      title: "Final-Year Students",
      subtitle: "B.Tech, BCA, BBA, MBA, M.Tech, etc.",
      description: "Preparing for campus placements",
      useCase: "MockTrail helps them understand how real interviews work, with role-specific practice",
      icon: <Users className="h-6 w-6" />
    },
    {
      title: "Job Seekers",
      subtitle: "Freshers or Professionals",
      description: "Recently graduated or in career transition",
      useCase: "Preparing for walk-ins, off-campus, or startup hiring. Often facing interview anxiety or lack of feedback",
      icon: <Target className="h-6 w-6" />
    },
    {
      title: "Working Professionals",
      subtitle: "1–5 Years Experience",
      description: "Looking to switch jobs or get promotions",
      useCase: "Need to refresh skills, especially system design, product, or leadership interviews",
      icon: <TrendingUp className="h-6 w-6" />
    },
    {
      title: "Career Changers",
      subtitle: "Industry Switchers",
      description: "People switching industries (e.g. from non-tech to tech)",
      useCase: "Need customized interview prep and resume-based feedback",
      icon: <Star className="h-6 w-6" />
    }
  ];

  const coreFeatures = [
    {
      title: "AI-Powered Interviews",
      description: "Realistic video/audio interviews with real-time responses",
      icon: <Video className="h-5 w-5" />
    },
    {
      title: "JD/Role-Based Interview Customization",
      description: "User enters role, job description, round type, and MockTrail creates the perfect simulation",
      icon: <Target className="h-5 w-5" />
    },
    {
      title: "Detailed Feedback",
      description: "Get breakdown of strengths, weaknesses, and improvement tips",
      icon: <BarChart3 className="h-5 w-5" />
    },
    {
      title: "Skill-Based News Feed",
      description: "Daily feed based on skills extracted from your resume",
      icon: <Newspaper className="h-5 w-5" />
    },
    {
      title: "Practice Recommendations",
      description: "AI recommends interview practice types based on resume matching",
      icon: <Brain className="h-5 w-5" />
    },
    {
      title: "15+ Realistic AI Interviewers",
      description: "Various AI Interviewers to simulate your interview experience - like a real interview",
      icon: <Video className="h-5 w-5" />
    }
  ];

  const bonusBenefits = [
    {
      title: "Available 24/7 - No waiting time",
      icon: <Clock className="h-5 w-5" />
    },
    {
      title: "First 20 minutes free",
      icon: <Gift className="h-5 w-5" />
    },
    {
      title: "Plans starting at ₹1999/month",
      icon: <DollarSign className="h-5 w-5" />
    }
  ];

  const promotionPlatforms = [
    {
      name: "WhatsApp groups",
      description: "College, alumni, job updates",
      icon: <MessageSquare className="h-5 w-5" />
    },
    {
      name: "LinkedIn",
      description: "Create posts, DM batchmates",
      icon: <Share2 className="h-5 w-5" />
    },
    {
      name: "Instagram",
      description: "Use story templates, before-after reels",
      icon: <Smartphone className="h-5 w-5" />
    },
    {
      name: "Telegram",
      description: "Job channels or placement groups",
      icon: <MessageSquare className="h-5 w-5" />
    },
    {
      name: "College events",
      description: "Student clubs and campus activities",
      icon: <Users className="h-5 w-5" />
    }
  ];

  const promotionTips = [
    "Use your own referral code in every post/message",
    "Make the people realize the need of Mock interviews practice",
    "Mention the free 20-minute trial to remove get them to sign up quickly",
    "Always plan the flow of the conversation before you start",
    "Personalize the message by sharing your own experience of MockTrail",
  ];

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-light/5 to-transparent dark:via-primary-dark/5"></div>
      {/* <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(37,99,235,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.05),transparent_50%)]"></div> */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(16,185,129,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_70%_50%,rgba(52,211,153,0.05),transparent_50%)]"></div>
      
      {/* Floating particles */}
      <div className="absolute left-[20%] top-[15%] w-5 h-5 rounded-full bg-primary-light/15 dark:bg-primary-dark/15 blur-md animate-float" style={{ animationDelay: '0.5s', animationDuration: '6s' }}></div>
      <div className="absolute right-[25%] top-[30%] w-4 h-4 rounded-full bg-secondary-light/15 dark:bg-secondary-dark/15 blur-md animate-float" style={{ animationDelay: '0.3s', animationDuration: '5s' }}></div>
      
      {/* Diagonal accent lines */}
      {/* <div className="absolute -left-32 top-52 w-[600px] h-[1px] bg-gradient-to-r from-transparent via-primary-light/15 to-transparent rotate-[25deg]"></div> */}
      {/* <div className="absolute -right-32 top-96 w-[600px] h-[1px] bg-gradient-to-r from-transparent via-secondary-light/15 to-transparent -rotate-[25deg]"></div>
       */}
      <ParticleBackground />
      <Navbar />
      
      <div className="container mx-auto px-4 py-12 max-w-7xl mt-36 relative z-10">
        {/* Header Section */}
        <div className={`text-center mb-16 transform transition-all duration-500 ease-out ${
          animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <div className="inline-flex items-center gap-2 bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Award className="h-4 w-4" />
            MockTrail Ambassador Program
          </div>
          <h1 className="text-5xl font-heading font-bold mb-6 bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
            Official Guide & Playbook
          </h1>
          <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto leading-relaxed">
            Welcome to the team! This guide will help you understand the product, the audience, and how to promote it effectively. 
            Let's help students and professionals ace their interviews — and earn while doing it!
          </p>
        </div>

        {/* About MockTrail Section */}
        <div className={`mb-20 transform transition-all duration-500 ease-out ${
          animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <div className="group relative rounded-2xl transition-transform duration-300 ease-out">
            {/* Animated border gradient */}
            {/* <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl animate-gradient bg-[length:200%_auto]" /> */}
            
            {/* Glass container */}
            <div className="relative h-full p-6 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">About MockTrail</h2>
              </div>
              <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-6 leading-relaxed">
                MockTrail is an AI-powered mock interview platform that helps users practice real interviews with AI through:
              </p>
              
              {/* Core Features Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {coreFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-background-light/50 dark:bg-background-dark/50 rounded-lg border border-border-light dark:border-border-dark hover:scale-[1.02] transition-transform duration-300 ease-out">
                    
                    <div className="p-2 text-primary-light dark:text-primary-dark bg-primary-light/10 dark:bg-primary-dark/10 rounded-lg flex-shrink-0">
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-text-primary-light dark:text-text-primary-dark mb-1">
                        {feature.title}
                      </h4>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bonus Benefits */}
              <div className="border-t border-border-light dark:border-border-dark pt-6">
                <h4 className="font-semibold text-text-primary-light dark:text-text-primary-dark mb-4">Bonus Benefits:</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  {bonusBenefits.map((benefit, index) => (
                    <div key={index} className="group relative rounded-2xl transition-transform duration-300 ease-out hover:scale-[1.02]">
                      {/* Animated border gradient */}
                      {/* <div className=" absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl animate-gradient bg-[length:200%_auto]" /> */}
                      
                      {/* Glass container */}
                      <div className="relative h-full p-4 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark">
                        <div className="flex items-center gap-3">
                          <div className="p-2 text-primary-light dark:text-primary-dark bg-primary-light/10 dark:bg-primary-dark/10 rounded-lg">
                            {benefit.icon}
                          </div>
                          <span className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                            {benefit.title}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-base text-text-primary-light dark:text-text-primary-dark mt-6">
                We help users practice smarter, gain confidence, and get job-ready.
              </p>
            </div>
          </div>
        </div>

        {/* Target Audience Section */}
        <div className={`mb-20 transform transition-all duration-500 ease-out ${
          animationPhase >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-text-primary-light dark:text-text-primary-dark">Who Should You Target?</h2>
            <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto">
              Focus on people actively preparing for interviews, such as:
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {targetAudiences.map((audience, index) => (
              <div key={index} className="group relative rounded-2xl transition-transform duration-300 ease-out hover:scale-[1.02] h-full">
                {/* Animated border gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl animate-gradient bg-[length:200%_auto]" />
                
                {/* Glass container */}
                <div className="relative h-full p-6 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark flex flex-col">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-3 rounded-xl bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark flex-shrink-0">
                      {audience.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-1">
                        {audience.title}
                      </h3>
                      <p className="text-base font-medium text-primary-light dark:text-primary-dark mb-2">
                        {audience.subtitle}
                      </p>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-3">
                        {audience.description}
                      </p>
                    </div>
                  </div>
                  <div className="bg-primary-light/5 dark:bg-primary-dark/5 rounded-lg p-4 border-l-4 border-primary-light dark:border-primary-dark mt-auto">
                    <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                      Use cases:
                    </p>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                      {audience.useCase}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Commission & Rewards Section */}
        <div className="mb-20">
          <div className="group relative rounded-2xl transition-transform duration-300 ease-out">
            {/* Animated border gradient */}
            {/* <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl animate-gradient bg-[length:200%_auto]" /> */}
            
            {/* Glass container */}
            <div className="relative h-full p-4 md:p-8 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark">
              <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-2 mb-4 mt-4">
                  <DollarSign className="hidden md:block h-8 w-8 text-primary-light dark:text-primary-dark" />
                  <h2 className="text-3xl  font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
                    Commissions & Rewards
                  </h2>
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="group relative rounded-2xl transition-transform duration-300 ease-out hover:scale-[1.02]">
                  {/* Animated border gradient */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl animate-gradient bg-[length:200%_auto]" />
                  
                  {/* Glass container */}
                  <div className="relative h-full p-6 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark text-center">
                    <div className="w-16 h-16 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <DollarSign className="h-8 w-8 text-primary-light dark:text-primary-dark" />
                    </div>
                    <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                      10–20% Commission
                    </h3>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                      On every paid subscription that your user code
                    </p>
                  </div>
                </div>
                
                <div className="group relative rounded-2xl transition-transform duration-300 ease-out hover:scale-[1.02]">
                  {/* Animated border gradient */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl animate-gradient bg-[length:200%_auto]" />
                  
                  {/* Glass container */}
                  <div className="relative h-full p-6 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark text-center">
                    <div className="w-16 h-16 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Gift className="h-8 w-8 text-primary-light dark:text-primary-dark" />
                    </div>
                    <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                      Milestone Bonuses
                    </h3>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                      5, 10, and 25 sales milestones like Merchandise, Swag, etc.
                    </p>
                  </div>
                </div>
                
                <div className="group relative rounded-2xl transition-transform duration-300 ease-out hover:scale-[1.02]">
                  {/* Animated border gradient */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl animate-gradient bg-[length:200%_auto]" />
                  
                  {/* Glass container */}
                  <div className="relative h-full p-6 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark text-center">
                    <div className="w-16 h-16 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Award className="h-8 w-8 text-primary-light dark:text-primary-dark" />
                    </div>
                    <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                      Top Ambassador Perks
                    </h3>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                      Internship letters, certificates, LORs, and MockTrail swag
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How to Promote Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-text-primary-light dark:text-text-primary-dark">How to Promote</h2>
            <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto">
              Use these platforms and strategies to reach your target audience effectively
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Platforms */}
            <div className="group relative rounded-2xl transition-transform duration-300 ease-out ">
              {/* Animated border gradient */}
              {/* <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl animate-gradient bg-[length:200%_auto]" /> */}
              
              {/* Glass container */}
              <div className="relative h-full p-6 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark">
                <h3 className="text-2xl font-heading font-bold mb-6 flex items-center gap-2 text-text-primary-light dark:text-text-primary-dark">
                  <Share2 className="h-6 w-6 text-primary-light dark:text-primary-dark" />
                  Platforms
                </h3>
                <div className="space-y-4">
                  {promotionPlatforms.map((platform, index) => (
                    <div key={index} className=" hover:scale-[1.02] transition-transform duration-300 ease-out flex items-start gap-4 p-4 bg-background-light/50 dark:bg-background-dark/30 rounded-lg border border border-border-light dark:border-border-dark">
                      <div className="p-2 text-primary-light dark:text-primary-dark bg-primary-light/10 dark:bg-primary-dark/10 rounded-lg flex-shrink-0">
                        {platform.icon}
                      </div>
                      <div>
                        <h4 className="font-semibold text-text-primary-light dark:text-text-primary-dark mb-1">
                          {platform.name}
                        </h4>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                          {platform.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="group relative rounded-2xl transition-transform duration-300 ease-out ">
              {/* Animated border gradient */}
              {/* <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl animate-gradient bg-[length:200%_auto]" /> */}
              
              {/* Glass container */}
              <div className="relative h-full p-6 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark">
                <h3 className="text-2xl font-heading font-bold mb-6 flex items-center gap-2 text-text-primary-light dark:text-text-primary-dark">
                  <Lightbulb className="h-6 w-6 text-primary-light dark:text-primary-dark" />
                  Tips
                </h3>
                <div className="space-y-4">
                  {promotionTips.map((tip, index) => (
                    <div key={index} className="hover:scale-[1.02] transition-transform duration-300 ease-out flex items-start gap-3 p-4 bg-background-light/50 dark:bg-background-dark/30 rounded-lg border border-border-light dark:border-border-dark">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm mt-0.5 text-text-secondary-light dark:text-text-secondary-dark">
                        {tip}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* First Goal Section */}
        <div className="mb-20">
          <div className="group relative rounded-2xl transition-transform duration-300 ease-out hover:scale-[1.02]">
            {/* Animated border gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl animate-gradient bg-[length:200%_auto]" />
            
            {/* Glass container */}
            <div className="relative h-full p-8 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Target className="hidden md:block h-8 w-8 text-primary-light dark:text-primary-dark" />
                <h2 className="text-3xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
                  Your First Goal
                </h2>
              </div>
              <p className="text-xl font-bold text-primary-light dark:text-primary-dark mb-4">
                Get 5 paid users in the first 2 weeks.
              </p>
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-base text-text-secondary-light dark:text-text-secondary-dark">
                <span className="flex items-center gap-2">
                  <UserCheck className="h-5 w-5 text-text-primary-light dark:text-text-primary-dark" />
                  Use your code
                </span>
                <span className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-text-primary-light dark:text-text-primary-dark" />
                  Track your sales
                </span>
                <span className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-text-primary-light dark:text-text-primary-dark" />
                  Climb the leaderboard
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="group relative rounded-2xl transition-transform duration-300 ease-out hover:scale-[1.02]">
            {/* Animated border gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl animate-gradient bg-[length:200%_auto]" />
            
            {/* Glass container */}
            <div className="relative h-full p-8 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark">
              <h2 className="text-3xl font-heading font-bold mb-4 text-text-primary-light dark:text-text-primary-dark">Ready to Start?</h2>
              <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-6 max-w-2xl mx-auto">
                Join the MockTrail Ambassador Program and start earning while helping others succeed in their interview journey.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => {
                    navigate('/ambassador-program');
                  }}
                  leftIcon={<Zap className="h-5 w-5" />}
                  rightIcon={<ArrowRight className="h-4 w-4" />}
                  className="w-full sm:w-auto"
                >
                  Apply For Ambassador
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  leftIcon={<ExternalLink className="h-5 w-5" />}
                  className="w-full sm:w-auto"
                  onClick={() => {
                    // Open in new tab
                    window.open('https://platform.mocktrail.app/settings-preferences', '_blank');
                  }}
                >
                  Manage Your Referrals
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmbassadorGuide; 